import React, { createContext, memo, useCallback, useState, type PropsWithChildren } from 'react';
import noop from 'lodash/noop';
import { v4 as uuid } from 'uuid';
import { isShallowEqual } from '@atlassian/jira-platform-shallow-equal/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import { createStore, createContainer } from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { actions, handleSearchInputUpdate } from './actions.tsx';
import type { State, Actions, Props, LifecycleState } from './types.tsx';

const initialState: State = {
	disposables: [],
	isFetching: false,
	isFetchingView: false,
	isRefreshing: false,
	isNetworkError: false,
	searchKey: '',
	totalIssueCount: null,
	totalIssueCountIsFetching: false,
	totalIssueCountIsError: false,
	inFlightIssueSearchInput: null,
};

const defaultLifecycleState: LifecycleState = {
	isFetching: false,
	isFetchingView: false,
	isRefreshing: false,
	isNetworkError: false,
	searchKey: '',
};
export const LifecycleContext = createContext<LifecycleState>(defaultLifecycleState);

type IssueSearchServiceWithLifecycleProps = PropsWithChildren<Omit<Props, 'onLifecycleUpdate'>>;
const IssueSearchServiceWithLifecycle = (props: IssueSearchServiceWithLifecycleProps) => {
	const [value, setValue] = useState(() => ({ ...defaultLifecycleState, searchKey: uuid() }));
	const onLifecycleUpdate = useCallback((newValue: Partial<LifecycleState>) => {
		setValue((prev) => ({ ...prev, ...newValue }));
	}, []);

	return (
		<LifecycleContext.Provider value={value}>
			<IssueSearchServiceContainerRSS {...props} onLifecycleUpdate={onLifecycleUpdate} />
		</LifecycleContext.Provider>
	);
};

const IssueSearchServiceContainerRSS = createContainer<Props>();

export const IssueSearchServiceContainer = componentWithFG(
	'jira_nin_refetch_state_concurrency_fix',
	memo(IssueSearchServiceWithLifecycle),
	(props: IssueSearchServiceWithLifecycleProps) => (
		<IssueSearchServiceContainerRSS {...props} onLifecycleUpdate={noop} />
	),
);

export const Store = createStore<State, Actions, Props>({
	name: 'IssueSearchServiceStore',
	initialState,
	actions,
	containedBy: IssueSearchServiceContainerRSS,
	handlers: {
		onInit:
			() =>
			({ setState }) => {
				if (!fg('jira_nin_refetch_state_concurrency_fix')) {
					setState({
						searchKey: uuid(),
					});
				}
			},
		onContainerUpdate:
			(nextProps, prevProps) =>
			({ dispatch, getState }) => {
				if (
					isShallowEqual(nextProps.issueSearchInput, getState().inFlightIssueSearchInput) &&
					fg('jira_list_nin_merge_technical_changes')
				) {
					return;
				}

				if (!isShallowEqual(nextProps.issueSearchInput, prevProps.issueSearchInput)) {
					dispatch(handleSearchInputUpdate());
				}
			},
		onDestroy:
			() =>
			({ getState }) => {
				const { disposables } = getState();
				// Dispose of any manually retained operations when the container is unmounted
				disposables.forEach((disposable) => disposable.dispose());
			},
	},
});
